import cn from 'classnames';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import ChevronRightIcon from '@icons/24/arrow-right-for-breadcrumbs.svg';

import styles from './Breadcrumbs.module.scss';
import { BreadcrumbsProps } from './types';

const DEFAULT_ARIA_LABEL = 'Вы находитесь здесь:';
const SCROLL_END_THRESHOLD_PX = 2;

export const Breadcrumbs = ({
    items,
    scrollable = true,
    isLastItemLink = false,
    withPageOffset = false,
    ariaLabel = DEFAULT_ARIA_LABEL,
    className,
    ...rest
}: BreadcrumbsProps) => {
    const listRef = useRef<HTMLOListElement>(null);
    const [showEndFade, setShowEndFade] = useState(false);

    useEffect(() => {
        if (!scrollable) {
            setShowEndFade(false);

            return;
        }

        const list = listRef.current;

        if (!list) {
            return;
        }

        const updateFade = () => {
            const maxScrollLeft = list.scrollWidth - list.clientWidth;
            const hasOverflow = maxScrollLeft > SCROLL_END_THRESHOLD_PX;
            const isAtEnd = list.scrollLeft >= maxScrollLeft - SCROLL_END_THRESHOLD_PX;

            setShowEndFade(hasOverflow && !isAtEnd);
        };

        updateFade();
        list.addEventListener('scroll', updateFade, { passive: true });
        window.addEventListener('resize', updateFade);

        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateFade) : undefined;
        resizeObserver?.observe(list);

        return () => {
            list.removeEventListener('scroll', updateFade);
            window.removeEventListener('resize', updateFade);
            resizeObserver?.disconnect();
        };
    }, [items, scrollable]);

    if (!items.length) {
        return null;
    }

    return (
        <nav
            aria-label={ariaLabel}
            className={cn(styles.breadcrumbs, withPageOffset && styles.breadcrumbs_pageOffset, className)}
            {...rest}
        >
            <div
                className={cn(
                    scrollable && styles.breadcrumbs_scrollWrap,
                    scrollable && showEndFade && styles.breadcrumbs_scrollWrap_fade
                )}
            >
                <ol
                    ref={listRef}
                    className={cn(styles.breadcrumbs_list, scrollable && styles.breadcrumbs_scrollable)}
                    itemScope
                    itemType="https://schema.org/BreadcrumbList"
                >
                    {items.map((item, index) => {
                        const isLast = index === items.length - 1;
                        const isBeforeLast = index === items.length - 2;
                        const showLink = Boolean(item.href) && (!isLast || isLastItemLink);
                        const position = index + 1;

                        return (
                            <li
                                key={`${position}-${typeof item.label === 'string' ? item.label : 'item'}`}
                                className={styles.breadcrumbs_item}
                                itemProp="itemListElement"
                                itemScope
                                itemType="https://schema.org/ListItem"
                            >
                                {showLink && item.href ? (
                                    <Link
                                        href={item.href}
                                        prefetch={false}
                                        className={styles.breadcrumbs_link}
                                        itemProp="item"
                                    >
                                        <span itemProp="name">{item.label}</span>
                                    </Link>
                                ) : (
                                    <span
                                        className={styles.breadcrumbs_current}
                                        itemProp="name"
                                        {...(isLast ? { 'aria-current': 'page' as const } : {})}
                                    >
                                        {item.label}
                                    </span>
                                )}

                                {!isLast ? (
                                    <span
                                        className={cn(
                                            styles.breadcrumbs_separator,
                                            isBeforeLast && styles.breadcrumbs_separator_beforeLast
                                        )}
                                        aria-hidden
                                    >
                                        <ChevronRightIcon className={styles.breadcrumbs_separatorIcon} />
                                    </span>
                                ) : null}

                                <meta itemProp="position" content={String(position)} />
                            </li>
                        );
                    })}
                </ol>
            </div>
        </nav>
    );
};
